import type { AnyFieldApi } from "@tanstack/react-form"
import type { ReactNode } from "react"

import {
  AmountInput,
  type AmountUnit,
} from "@repo/ui/components/ui/amount-input"
import { Checkbox } from "@repo/ui/components/ui/checkbox"
import {
  CountrySelect,
  type CountryCode,
} from "@repo/ui/components/ui/country-select"
import { DatePicker } from "@repo/ui/components/ui/date-picker"
import { DecimalInput } from "@repo/ui/components/ui/decimal-input"
import { Field, FieldLabel, FieldMessage } from "@repo/ui/components/ui/field"
import { If } from "@repo/ui/components/ui/if"
import { Input } from "@repo/ui/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@repo/ui/components/ui/input-group"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@repo/ui/components/ui/input-otp"
import {
  NumberStepper,
  type NumberStepperProps,
} from "@repo/ui/components/ui/number-stepper"
import {
  OptionPicker,
  type OptionPickerOption,
} from "@repo/ui/components/ui/option-picker"
import { PercentageInput } from "@repo/ui/components/ui/percentage-input"
import { RadioGroup, RadioGroupItem } from "@repo/ui/components/ui/radio-group"
import { Switch } from "@repo/ui/components/ui/switch"
import { TagsInput } from "@repo/ui/components/ui/tags-input"
import { Textarea } from "@repo/ui/components/ui/textarea"
import { cn } from "@repo/ui/lib/utils"

type BaseFieldProps = {
  field: AnyFieldApi
  label: string
  description?: string
  /** Styles the field WRAPPER — layout and spacing around label + control. */
  className?: string
  /**
   * Styles the CONTROL itself — the input/picker/textarea, for sizing a field
   * that needs to differ (`min-h-40` on a notes textarea). A field whose control
   * takes no class ignores it rather than erroring, so it is safe to pass
   * generically.
   */
  inputClassName?: string
}

type DataAttributes = {
  [key: `data-${string}`]: string | boolean | undefined
}

interface TextFieldProps extends BaseFieldProps {
  placeholder?: string
  type?: React.HTMLInputTypeAttribute
  disabled?: boolean
  inputProps?: Omit<
    React.ComponentProps<typeof Input>,
    "id" | "name" | "value" | "onChange" | "onBlur" | "aria-invalid"
  > &
    DataAttributes
}

export function TextField({
  field,
  label,
  placeholder,
  type,
  disabled,
  description,
  className,
  inputClassName,
  inputProps,
}: TextFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <Input
        id={field.name}
        name={field.name}
        className={inputClassName}
        type={type}
        placeholder={placeholder}
        value={field.state.value ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        disabled={disabled}
        aria-invalid={field.state.meta.errors.length > 0}
        {...inputProps}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface NumberFieldProps extends BaseFieldProps {
  placeholder?: string
  disabled?: boolean
  /** A unit or symbol in a leading addon. */
  prefix?: ReactNode
  /** A fixed unit in a trailing addon: `days`, `kg`, `%`. */
  suffix?: ReactNode
  min?: number
  max?: number
}

export function NumberField({
  field,
  label,
  placeholder,
  disabled,
  prefix,
  suffix,
  min,
  max,
  description,
  className,
  inputClassName,
}: NumberFieldProps) {
  const invalid = field.state.meta.errors.length > 0
  const control = {
    id: field.name,
    name: field.name,
    type: "number" as const,
    placeholder,
    min,
    max,
    value: (field.state.value ?? "") as string | number,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      // `null`, not `undefined`: the update pipeline reads `undefined` as
      // "not in this patch", so a cleared number would never clear (#189).
      field.handleChange(
        event.currentTarget.value === ""
          ? null
          : event.currentTarget.valueAsNumber,
      )
    },
    onBlur: field.handleBlur,
    disabled,
    "aria-invalid": invalid,
  }
  return (
    <Field data-invalid={invalid} className={className}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <If
        condition={prefix != null || suffix != null}
        else={<Input {...control} className={inputClassName} />}
      >
        <InputGroup aria-invalid={invalid} data-disabled={disabled}>
          <If condition={prefix != null}>
            <InputGroupAddon align="inline-start" variant="filled">
              {prefix}
            </InputGroupAddon>
          </If>
          <InputGroupInput {...control} className={inputClassName} />
          <If condition={suffix != null}>
            <InputGroupAddon align="inline-end" variant="filled">
              {suffix}
            </InputGroupAddon>
          </If>
        </InputGroup>
      </If>
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface NumberStepperFieldProps extends BaseFieldProps {
  disabled?: boolean
  min?: number
  max?: number
  step?: number
  size?: "sm" | "default"
  variant?: NumberStepperProps["variant"]
}

/** The field's value is a number; one that holds none shows empty, like `NumberField`. */
export function NumberStepperField({
  field,
  label,
  disabled,
  min = 0,
  max,
  step,
  size,
  variant,
  description,
  className,
  inputClassName,
}: NumberStepperFieldProps) {
  const invalid = field.state.meta.errors.length > 0
  return (
    <Field data-invalid={invalid} className={className}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <NumberStepper
        id={field.name}
        name={field.name}
        className={inputClassName}
        value={typeof field.state.value === "number" ? field.state.value : null}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        min={min}
        max={max}
        step={step}
        size={size}
        variant={variant}
        disabled={disabled}
        aria-invalid={invalid}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface PercentageInputFieldProps extends BaseFieldProps {
  placeholder?: string
  disabled?: boolean
  min?: number
  max?: number
}

export function PercentageInputField({
  field,
  label,
  placeholder,
  disabled,
  min,
  max,
  description,
  className,
  inputClassName,
}: PercentageInputFieldProps) {
  const value =
    typeof field.state.value === "number" ? field.state.value : undefined

  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <PercentageInput
        id={field.name}
        name={field.name}
        className={inputClassName}
        min={min}
        max={max}
        value={value}
        onPercentageChange={field.handleChange}
        onBlur={field.handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={field.state.meta.errors.length > 0}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface DecimalInputFieldProps extends BaseFieldProps {
  placeholder?: string
  disabled?: boolean
  maxDecimalPlaces?: number
}

export function DecimalInputField({
  field,
  label,
  placeholder,
  disabled,
  maxDecimalPlaces,
  description,
  className,
  inputClassName,
}: DecimalInputFieldProps) {
  const value = typeof field.state.value === "number" ? field.state.value : null

  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <DecimalInput
        id={field.name}
        name={field.name}
        className={inputClassName}
        value={value}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxDecimalPlaces={maxDecimalPlaces}
        aria-invalid={field.state.meta.errors.length > 0}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface AmountFieldProps extends BaseFieldProps {
  placeholder?: string
  disabled?: boolean
  /** Currency symbol, word or icon shown in a leading addon. */
  prefix?: ReactNode
  /** A fixed unit in a trailing addon: `%`, `days`, a currency code. */
  suffix?: ReactNode
  /** A unit the user chooses; the trailing cap is the menu. Wins over `suffix`. */
  units?: readonly AmountUnit[]
  unit?: string
  onUnitChange?: (unit: string) => void
  /** Decimal places the VALUE may carry; typing past it truncates. Default 2. */
  scale?: number
  /** Decimal places the blurred display shows. Default 2. */
  displayDecimals?: number
  /** Pins the display locale; defaults to the browser's after mount. */
  locale?: string
}

export function AmountField({
  field,
  label,
  placeholder,
  disabled,
  prefix,
  suffix,
  units,
  unit,
  onUnitChange,
  scale,
  displayDecimals,
  locale,
  description,
  className,
  inputClassName,
}: AmountFieldProps) {
  const value = field.state.value as number | string | null | undefined

  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <AmountInput
        id={field.name}
        name={field.name}
        className={inputClassName}
        value={value}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        prefix={prefix}
        suffix={suffix}
        units={units}
        unit={unit}
        onUnitChange={onUnitChange}
        scale={scale}
        displayDecimals={displayDecimals}
        locale={locale}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={field.state.meta.errors.length > 0}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface TextareaFieldProps extends BaseFieldProps {
  placeholder?: string
  disabled?: boolean
}

export function TextareaField({
  field,
  label,
  placeholder,
  disabled,
  description,
  className,
  inputClassName,
}: TextareaFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <Textarea
        id={field.name}
        name={field.name}
        placeholder={placeholder}
        value={field.state.value ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        disabled={disabled}
        // `min-h-*` here opens it as a writing surface; the base is
        // `field-sizing-content`, so it still grows with what is typed.
        className={inputClassName}
        aria-invalid={field.state.meta.errors.length > 0}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface OptionPickerFieldBaseProps extends BaseFieldProps {
  options: readonly OptionPickerOption[]
  placeholder?: string
  disabled?: boolean
  buttonGroupThreshold?: number
  forceMode?: "select" | "button-group"
  size?: "default" | "sm"
  searchable?: boolean
  searchPlaceholder?: string
  popoverWidth?: "contain" | "auto"
  clearable?: boolean
  /** Offers a "create a new option" row; the caller owns the write. */
  onCreate?: (term: string) => void
  createLabel?: string
  /**
   * Drives SERVER-SIDE filtering: the picker skips its built-in client-side
   * filtering and the owner feeds back already-filtered `options`. Forwarded so
   * a searched field is the same control as every other option-backed one
   * rather than a second picker that merely looks similar.
   */
  onSearchTermChange?: (term: string) => void
}

interface OptionPickerFieldSingleProps extends OptionPickerFieldBaseProps {
  multiple?: false
}

interface OptionPickerFieldMultiProps extends OptionPickerFieldBaseProps {
  multiple: true
}

type OptionPickerFieldProps =
  | OptionPickerFieldSingleProps
  | OptionPickerFieldMultiProps

/**
 * A multi picker maps over its value, so anything that isn't an array throws on
 * render — a real risk for a column that used to hold free text and is now a
 * declared set. Coerced at the seam rather than trusting every writer.
 */
function toValueArray(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[]
  if (value == null || value === "") return []
  return [String(value)]
}

export function OptionPickerField({
  field,
  label,
  description,
  className,
  inputClassName,
  multiple,
  ...pickerProps
}: OptionPickerFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          field.handleBlur()
        }
      }}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      {multiple ? (
        <OptionPicker
          multiple
          id={field.name}
          value={toValueArray(field.state.value)}
          onValueChange={field.handleChange}
          aria-invalid={field.state.meta.errors.length > 0}
          className={inputClassName}
          {...pickerProps}
        />
      ) : (
        <OptionPicker
          id={field.name}
          value={field.state.value}
          onValueChange={field.handleChange}
          aria-invalid={field.state.meta.errors.length > 0}
          className={inputClassName}
          {...pickerProps}
        />
      )}
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

type SelectOption = {
  label: string
  value: string
  description?: string
  /** A muted glyph at the option row's end — a fact about the option. */
  endIcon?: ReactNode
}

interface SearchSelectFieldProps extends BaseFieldProps {
  /** The CURRENT page of matches — a searched field never holds the whole set. */
  options: readonly SelectOption[]
  placeholder?: string
  isLoading?: boolean
  /** Fires as the user types; the owner re-queries and feeds back `options`. */
  onSearchTermChange?: (term: string) => void
  onCreate?: (term: string) => void
  createLabel?: string
}

/**
 * A single-select bound to a SEARCH rather than a fixed option list — for a
 * relation whose target is too large to enumerate into a picker.
 *
 * The selected option is remembered as options come and go: a search replaces
 * the list, and without this the current value would render blank the moment it
 * fell out of the latest page of results.
 */
export function SearchSelectField({
  field,
  label,
  description,
  className,
  inputClassName,
  options,
  placeholder,
  isLoading,
  onSearchTermChange,
  onCreate,
  createLabel,
}: SearchSelectFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <OptionPicker
        id={field.name}
        value={
          field.state.value == null || field.state.value === ""
            ? null
            : String(field.state.value)
        }
        onValueChange={(next) => field.handleChange(next ?? "")}
        // No local memo of what was selected: the picker remembers every
        // option it has been handed, so a label survives the search that
        // replaced the list. Rebuilding it here is what printed a raw id.
        options={options}
        searchable
        onSearchTermChange={onSearchTermChange}
        isLoading={isLoading}
        onCreate={onCreate}
        createLabel={createLabel}
        placeholder={placeholder}
        className={inputClassName}
        // A searched relation is never a button group, however few matches
        // the current page happens to hold.
        forceMode="select"
        clearable
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface SelectFieldProps extends BaseFieldProps {
  options: readonly SelectOption[]
  placeholder?: string
  disabled?: boolean
  /**
   * Show a clear button once a value is chosen — a select has no other way back
   * to unset, so an OPTIONAL field needs this or its value becomes permanent.
   * Same affordance {@link DatePickerField} offers.
   */
  clearable?: boolean
}

export function SelectField({
  field,
  label,
  options,
  placeholder,
  disabled,
  description,
  className,
  inputClassName,
  clearable,
}: SelectFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      {/* OptionPicker, not the demoted Select: its rows carry a description
          as a second line, which the Select item never learned to size. */}
      <OptionPicker
        id={field.name}
        // A form select stays a dropdown however few options it holds — the
        // segmented group is the `button-group` input's shape, not this one's.
        forceMode="select"
        value={
          field.state.value != null && field.state.value !== ""
            ? // Coerce: an FK/enum value may be a number (e.g. roleId) while
              // option values are strings.
              String(field.state.value)
            : null
        }
        onValueChange={(value) => field.handleChange(value ?? "")}
        onBlur={field.handleBlur}
        options={options.map((option) => ({
          value: String(option.value),
          label: option.label,
          description: option.description,
          endIcon: option.endIcon,
        }))}
        placeholder={
          placeholder ?? `Select ${label.replace(/\s*\*$/, "").toLowerCase()}`
        }
        disabled={disabled}
        clearable={clearable}
        aria-invalid={field.state.meta.errors.length > 0}
        className={inputClassName}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface RadioGroupFieldProps extends BaseFieldProps {
  options: {
    value: string | number
    label: string
    description?: string
  }[]
  disabled?: boolean
}

/**
 * The bound radio list — one visible card row per option, each carrying its
 * consequence line. For the handful-of-states choice a select would hide
 * (a block category, a status change); past ~5 options reach for
 * {@link SelectField}.
 */
export function RadioGroupField({
  field,
  label,
  options,
  disabled,
  description,
  className,
}: RadioGroupFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <RadioGroup
        value={
          field.state.value != null && field.state.value !== ""
            ? String(field.state.value)
            : null
        }
        onValueChange={(value) => field.handleChange(value ?? "")}
        disabled={disabled}
      >
        {options.map((option) => (
          <RadioGroupItem
            key={String(option.value)}
            value={String(option.value)}
            variant="card"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-base font-medium">{option.label}</span>
              {option.description ? (
                <span className="text-subtle-foreground text-xs">
                  {option.description}
                </span>
              ) : null}
            </div>
          </RadioGroupItem>
        ))}
      </RadioGroup>
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface CountrySelectFieldProps extends BaseFieldProps {
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  /** Restrict the list, e.g. `["ZA", "UK", "US"]`. Omit for every country. */
  countries?: readonly CountryCode[]
}

/**
 * The bound country picker — {@link CountrySelect} over the shared country
 * list, virtualized and searchable so ~250 options stay usable.
 */
export function CountrySelectField({
  field,
  label,
  description,
  className,
  inputClassName,
  ...selectProps
}: CountrySelectFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          field.handleBlur()
        }
      }}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <CountrySelect
        value={(field.state.value as CountryCode | undefined) || undefined}
        onChange={field.handleChange}
        className={inputClassName}
        {...selectProps}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface SwitchFieldProps extends BaseFieldProps {
  disabled?: boolean
}

export function SwitchField({
  field,
  label,
  description,
  className,
  inputClassName,
  disabled,
}: SwitchFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <div className="flex items-center gap-2">
        <Switch
          id={field.name}
          checked={field.state.value}
          onCheckedChange={(checked) => field.handleChange(checked)}
          onBlur={field.handleBlur}
          disabled={disabled}
          className={inputClassName}
        />
        <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      </div>
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface OtpInputFieldProps extends BaseFieldProps {
  maxLength?: number
}

export function OtpInputField({
  field,
  label,
  description,
  className,
  inputClassName,
  maxLength = 6,
}: OtpInputFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <InputOTP
        maxLength={maxLength}
        containerClassName={cn("w-full", inputClassName)}
        id={field.name}
        name={field.name}
        value={typeof field.state.value === "string" ? field.state.value : ""}
        onChange={(value) => field.handleChange(value)}
        onBlur={field.handleBlur}
        onComplete={field.handleBlur}
        autoComplete="one-time-password"
      >
        <InputOTPGroup className="w-full">
          {Array.from({ length: maxLength }, (_, i) => (
            <InputOTPSlot key={i} index={i} className="min-h-12 w-1/6" />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface CheckboxFieldProps extends BaseFieldProps {
  disabled?: boolean
}

export function CheckboxField({
  field,
  label,
  description,
  className,
  inputClassName,
  disabled,
}: CheckboxFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          id={field.name}
          checked={field.state.value ?? false}
          onCheckedChange={(checked) => field.handleChange(checked === true)}
          onBlur={field.handleBlur}
          disabled={disabled}
          className={cn("mt-0.5", inputClassName)}
        />
        <label htmlFor={field.name} className="grid cursor-pointer gap-1">
          <span className="text-sm leading-none font-medium">{label}</span>
          {description && (
            <span className="text-muted-foreground text-sm leading-snug">
              {description}
            </span>
          )}
        </label>
      </div>
      <FieldMessage errors={field.state.meta.errors} />
    </Field>
  )
}

interface DatePickerFieldProps extends BaseFieldProps {
  minDate?: string
  maxDate?: string
  clearable?: boolean
  allowOutOfRangeInput?: boolean
  withTime?: boolean
  timeStep?: number
}

export function DatePickerField({
  field,
  label,
  description,
  className,
  inputClassName,
  minDate,
  maxDate,
  clearable,
  allowOutOfRangeInput,
  withTime,
  timeStep,
}: DatePickerFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          field.handleBlur()
        }
      }}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <DatePicker
        id={field.name}
        className={inputClassName}
        value={field.state.value ?? ""}
        onChange={(date) => field.handleChange(date)}
        minDate={minDate}
        maxDate={maxDate}
        allowOutOfRangeInput={allowOutOfRangeInput}
        clearable={clearable}
        withTime={withTime}
        timeStep={timeStep}
        aria-invalid={field.state.meta.errors.length > 0}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface TagsInputFieldProps extends BaseFieldProps {
  placeholder?: string
  disabled?: boolean
}

export function TagsInputField({
  field,
  label,
  description,
  className,
  inputClassName,
  placeholder,
  disabled,
}: TagsInputFieldProps) {
  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          field.handleBlur()
        }
      }}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <TagsInput
        // A form seeds an untouched list field as "", not an empty list.
        value={Array.isArray(field.state.value) ? field.state.value : []}
        className={inputClassName}
        onChange={(next) => field.handleChange(next)}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={field.state.meta.errors.length > 0}
      />
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}

interface ColorFieldProps extends BaseFieldProps {
  defaultColor: string
  placeholder: string
}

export function ColorField({
  field,
  label,
  description,
  className,
  inputClassName,
  defaultColor,
  placeholder,
}: ColorFieldProps) {
  const value = typeof field.state.value === "string" ? field.state.value : ""

  return (
    <Field
      data-invalid={field.state.meta.errors.length > 0}
      className={className}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          field.handleBlur()
        }
      }}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      {/* Two controls, so the class sizes the PAIR — a caller reaching for
          `inputClassName` here means the field, not the swatch. */}
      <div className={cn("flex gap-2", inputClassName)}>
        <Input
          id={field.name}
          name={field.name}
          type="color"
          value={value || defaultColor}
          onChange={(e) => field.handleChange(e.target.value)}
          className="h-10 w-14 p-1"
          aria-invalid={field.state.meta.errors.length > 0}
        />
        <Input
          id={`${field.name}-text`}
          value={value}
          onChange={(e) => field.handleChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
          aria-invalid={field.state.meta.errors.length > 0}
        />
      </div>
      <FieldMessage
        description={description}
        errors={field.state.meta.errors}
      />
    </Field>
  )
}
